// Dashboard grid (pure): x/w in columns, y/h in rows, compacted upwards.

// Interfaces
export interface GridItem {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface GridLayout {
  columns: number;
  items: GridItem[];
}

// Former placement: index "t-0-g-{row}-i-{rank}", size in px.
export interface LegacyWidget {
  id: string;
  index?: string | null;
  width?: number | null;
  height?: number | null;
}

export interface GridSize {
  w: number;
  h: number;
}

// Variables
export const GRID_COLUMNS = 24;
export const GRID_ROW_HEIGHT = 30;   // px
export const GRID_GAP = 10;          // px
export const MIN_SIZE: GridSize = { w: 2, h: 2 };
export const MIN_ROW_HEIGHT = 10;    // px, fitted screens

// Widget without size: 8 x 8 cells (over 1200 px rows, the former minimum).
const LEGACY_DEFAULT = { width: 390, height: 310 };
const LEGACY_MIN_WIDTH = 1200;

// Classes
// Columns for a grid width: desktop, tablet, phone.
export function columnsFor(width: number): number {
  if (width >= 992) return GRID_COLUMNS;
  return width >= 576 ? GRID_COLUMNS / 2 : 1;
}

export const bottomOf = (items: GridItem[]): number => items.reduce((max, item) => Math.max(max, item.y + item.h), 0);

const overlapsX = (a: GridItem, b: GridItem): boolean => a.x < b.x + b.w && b.x < a.x + a.w;

export function collides(a: GridItem, b: GridItem): boolean {
  return a.id !== b.id && overlapsX(a, b) && a.y < b.y + b.h && b.y < a.y + a.h;
}

// Whole numbers, within the columns.
export function clampItem(item: GridItem, columns: number = GRID_COLUMNS): GridItem {
  const w = Math.min(columns, Math.max(1, Math.round(item.w)));
  const x = Math.min(columns - w, Math.max(0, Math.round(item.x)));
  return { id: item.id, x, y: Math.max(0, Math.round(item.y)), w, h: Math.max(1, Math.round(item.h)) };
}

// Reading order.
const byPosition = (a: GridItem, b: GridItem): number => a.y - b.y || a.x - b.x;

// Each widget as high as possible, reading order kept.
export function compact(items: GridItem[]): GridItem[] {
  const placed: GridItem[] = [];

  for (const item of [...items].sort(byPosition)) {
    const y = placed.filter(other => overlapsX(item, other)).reduce((top, other) => Math.max(top, other.y + other.h), 0);
    placed.push({ ...item, y });
  }

  return placed;
}

// Pushes overlapped widgets below the active one; a pushed one never pushes a higher one (it goes under).
function pushDown(items: GridItem[], active: GridItem): void {
  const queue = [active];

  while (queue.length) {
    const above = queue.shift()!;

    for (const item of items) {
      if (!collides(item, above) || (above !== active && item.y < above.y)) continue;

      item.y = above.y + above.h;

      for (let landed = true; landed;) {
        const higher = items.find(other => other !== item && other.y < item.y && collides(other, item));
        landed = !!higher;

        if (higher) 
          item.y = higher.y + higher.h;
      }
      
      queue.push(item);
    }
  }
}

// Active widget kept, the others pushed, then compacted.
function settle(items: GridItem[], active: GridItem): GridItem[] {
  const others = items.filter(item => item.id !== active.id).map(item => ({ ...item }));
  pushDown(others, active);
  return compact([active, ...others]);
}

// Upper half of a widget: takes its place; lower half: goes below it.
export function moveItem(items: GridItem[], id: string, x: number, y: number, columns: number = GRID_COLUMNS): GridItem[] {
  const current = items.find(item => item.id === id);

  if (!current) return items;

  const active = clampItem({ ...current, x, y }, columns);

  for (let moved = true; moved;) {
    moved = false;

    for (const other of items) {
      if (collides(active, other) && active.y >= other.y + other.h / 2) {
        active.y = other.y + other.h;
        moved = true;
      }
    }
  }

  return settle(items, active);
}

export function resizeItem(items: GridItem[], id: string, w: number, h: number, min: GridSize = MIN_SIZE, columns: number = GRID_COLUMNS): GridItem[] {
  const current = items.find(item => item.id === id);

  if (!current) return items;

  const active = clampItem({ ...current, w: Math.min(columns - current.x, Math.max(min.w, w)), h: Math.max(min.h, h) }, columns);
  return settle(items, active);
}

// Narrower grid: x and w scaled, then compacted (1 column: stacked).
export function deriveLayout(items: GridItem[], columns: number): GridItem[] {
  if (columns >= GRID_COLUMNS)
    return compact(items);

  const ratio = columns / GRID_COLUMNS;
  return compact([...items].sort(byPosition).map(item => clampItem({ ...item, x: item.x * ratio, w: Math.max(1, item.w * ratio) }, columns)));
}

const legacyWidth = (widget: LegacyWidget) => (widget.width && widget.width > 0 ? widget.width : LEGACY_DEFAULT.width);

// Former px size to columns/rows.
function legacySize(widget: LegacyWidget, columnWidth: number): GridSize {
  const width = legacyWidth(widget);
  const height = widget.height && widget.height > 0 ? widget.height : LEGACY_DEFAULT.height;

  return {
    w: Math.min(GRID_COLUMNS, Math.max(1, Math.round((width + GRID_GAP) / columnWidth))),
    h: Math.max(MIN_SIZE.h, Math.round((height + GRID_GAP) / (GRID_ROW_HEIGHT + GRID_GAP))),
  };
}

// Former rows as bands (ranks as numbers), widest row = 24 columns, unindexed widgets last.
export function fromLegacy(widgets: LegacyWidget[]): GridItem[] {
  const rows = new Map<number, { i: number; widget: LegacyWidget }[]>();
  const unindexed: LegacyWidget[] = [];

  for (const widget of widgets) {
    const match = /g-(\d+)-i-(\d+)/.exec(widget.index ?? '');

    if (!match) {
      unindexed.push(widget);
      continue;
    }

    const g = Number(match[1]);
    rows.set(g, [...(rows.get(g) ?? []), { i: Number(match[2]), widget }]);
  }

  const ordered = [...rows.keys()].sort((a, b) => a - b).map(g => rows.get(g)!.sort((a, b) => a.i - b.i).map(entry => entry.widget));

  if (unindexed.length)
    ordered.push(unindexed);

  const reference = Math.max(LEGACY_MIN_WIDTH, ...ordered.map(row => row.reduce((sum, widget) => sum + legacyWidth(widget) + GRID_GAP, -GRID_GAP)));
  const columnWidth = (reference + GRID_GAP) / GRID_COLUMNS;

  const items: GridItem[] = [];
  let y = 0;

  for (const row of ordered) {
    const sizes = row.map(widget => legacySize(widget, columnWidth));

    // Rounding overflow: the widest give a column back.
    while (sizes.reduce((sum, size) => sum + size.w, 0) > GRID_COLUMNS) {
      const widest = sizes.reduce((max, size) => (size.w > max.w ? size : max), sizes[0]!);

      if (widest.w === 1) break;

      widest.w--;
    }

    let x = 0;

    row.forEach((widget, index) => {
      const size = sizes[index]!;

      // Row wider than the grid: wraps.
      if (x + size.w > GRID_COLUMNS) {
        x = 0;
        y = bottomOf(items);
      }

      items.push({ id: widget.id, x, y, ...size });
      x += size.w;
    });

    y = bottomOf(items);
  }

  return compact(items);
}

// Saved layout (gone widgets dropped, new ones at the bottom), else converted from the former index.
export function resolveLayout(saved: GridItem[] | null | undefined, widgets: LegacyWidget[], columns: number = GRID_COLUMNS): GridItem[] {
  if (!saved) return fromLegacy(widgets);

  const ids = new Set(widgets.map(widget => widget.id));
  const items = saved.filter(item => ids.has(item.id)).map(item => clampItem(item, columns));
  const placed = new Set(items.map(item => item.id));
  const columnWidth = (LEGACY_MIN_WIDTH + GRID_GAP) / GRID_COLUMNS;

  for (const widget of widgets.filter(widget => !placed.has(widget.id))) {
    items.push(clampItem({ id: widget.id, x: 0, y: bottomOf(items), ...legacySize(widget, columnWidth) }, columns));
  }

  return items;
}

// Item size in px for a grid of that width.
export function toPixels(item: GridSize, gridWidth: number, columns: number = GRID_COLUMNS, rowHeight: number = GRID_ROW_HEIGHT): { width: number; height: number } {
  const columnWidth = (gridWidth - GRID_GAP * (columns - 1)) / columns;

  return {
    width: Math.round(item.w * columnWidth + (item.w - 1) * GRID_GAP),
    height: item.h * rowHeight + (item.h - 1) * GRID_GAP,
  };
}

// Row height filling that height with that many rows (big screens).
export function fitRowHeight(rows: number, height: number): number {
  return rows ? Math.max(MIN_ROW_HEIGHT, Math.floor((height - GRID_GAP * (rows - 1)) / rows)) : GRID_ROW_HEIGHT;
}

// Column and row pitches in px (cell + gap).
export const pitchOf = (gridWidth: number, columns: number = GRID_COLUMNS, rowHeight: number = GRID_ROW_HEIGHT) => ({
  x: (gridWidth + GRID_GAP) / columns,
  y: rowHeight + GRID_GAP,
});

// Nearest cell of a point in px (from the grid's top left).
export function cellAt(left: number, top: number, gridWidth: number, columns: number = GRID_COLUMNS): { x: number; y: number } {
  const pitch = pitchOf(gridWidth, columns);
  return { x: Math.max(0, Math.round(left / pitch.x)), y: Math.max(0, Math.round(top / pitch.y)) };
}

// Nearest span of a size in px.
export function spanOf(width: number, height: number, gridWidth: number, columns: number = GRID_COLUMNS): GridSize {
  const pitch = pitchOf(gridWidth, columns);
  return { w: Math.round((width + GRID_GAP) / pitch.x), h: Math.round((height + GRID_GAP) / pitch.y) };
}

// Pages of `rows` rows, cut where no widget spans the cut; a taller band scaled down to fit.
export function paginate(items: GridItem[], rows: number): { top: number; scale: number; items: GridItem[] }[] {
  // At least a row a page (very tall rows round to 0): else the loop never moves on.
  rows = Math.max(1, Math.floor(rows));
  const bottom = bottomOf(items);
  const crosses = (row: number) => items.some(item => item.y < row && row < item.y + item.h);
  const pages: { top: number; scale: number; items: GridItem[] }[] = [];

  for (let top = 0; top < bottom;) {
    let end = Math.min(top + rows, bottom);
    while (end > top + 1 && crosses(end)) end--;

    // Taller than a page: cut right after it.
    if (crosses(end))
      while (end < bottom && crosses(end)) end++;

    pages.push({ top, scale: Math.min(1, rows / (end - top)), items: items.filter(item => item.y >= top && item.y < end) });
    top = end;
  }

  return pages.filter(page => page.items.length);
}

// Same placement, whatever the order.
export function sameLayout(a: GridItem[], b: GridItem[]): boolean {
  if (a.length !== b.length) return false;

  const byId = new Map(b.map(item => [item.id, item]));

  return a.every(item => {
    const other = byId.get(item.id);
    return !!other && other.x === item.x && other.y === item.y && other.w === item.w && other.h === item.h;
  });
}
